import { useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import {
  TRIVY_LABELS,
  VulnerabilityReport,
  VulnerabilityReportList,
} from '../types';

import { queryKeys } from './queryKeys';

const TRIVY_API = 'apis/aquasecurity.github.io/v1alpha1';

// Addressed by label selector, not name: report names are derived
// ({kind}-{resource}-{container}) and awkward to reconstruct. Needs read access
// to aquasecurity.github.io, which today only admins have.
export function useVulnerabilityReports<T = Array<VulnerabilityReport>>({
  environmentId,
  namespace,
  resourceName,
  enabled = true,
  select,
}: {
  environmentId: EnvironmentId;
  namespace?: string;
  resourceName?: string;
  enabled?: boolean;
  select?: (reports: Array<VulnerabilityReport>) => T;
}) {
  return useQuery(
    queryKeys.vulnerabilityReports(environmentId, namespace, resourceName),
    () => getVulnerabilityReports(environmentId, namespace, resourceName),
    {
      ...withError('Unable to get vulnerability reports'),
      enabled: enabled && !!environmentId,
      select,
    }
  );
}

async function getVulnerabilityReports(
  environmentId: EnvironmentId,
  namespace?: string,
  resourceName?: string
): Promise<Array<VulnerabilityReport>> {
  const scope = namespace ? `namespaces/${namespace}/` : '';
  const params = resourceName
    ? { labelSelector: `${TRIVY_LABELS.resourceName}=${resourceName}` }
    : undefined;

  try {
    const { data } = await axios.get<VulnerabilityReportList>(
      `/endpoints/${environmentId}/kubernetes/${TRIVY_API}/${scope}vulnerabilityreports`,
      { params }
    );
    return data.items ?? [];
  } catch (error) {
    throw parseAxiosError(error);
  }
}
